/**
 * Welcher Bildschirm gilt — abgelesen am **Fragment** der Adresse.
 *
 * **Warum das Fragment und nicht der Pfad.** Ein Host- oder Einladungslink soll geteilt werden;
 * der Link selbst ist die Berechtigung (`CLAUDE.md`, Berechtigungsmodell). Genau deshalb darf
 * er nicht in einem Zugriffslog landen — und ein Pfad tut das: nginx protokolliert ihn, und der
 * Browser schickt ihn als `Referer` weiter. Das Fragment sendet der Browser **nie** an den
 * Server. Damit bleibt der Link teilbar, ohne eine Spur zu hinterlassen, die niemand gewollt hat.
 *
 * **Der Admin-Schlüssel steht in keiner Adresse.** Er ist dauerhaft und gehört nur dem User
 * (`CLAUDE.md`); ein Link damit stünde im Verlauf jedes Browsers, über den er je geöffnet wurde.
 * `#/admin` trägt deshalb keinen Token — er wird eingegeben.
 *
 * **Der Sitzungs-Token kommt hier gar nicht vor.** Er ist nicht zum Teilen gedacht; ein
 * geteilter Sitzungslink wäre die Übernahme eines Spielerplatzes. Er lebt im `localStorage`
 * (`session.ts`).
 *
 * **Kein Router als Bibliothek.** Welcher Bildschirm gilt, folgt aus zwei Dingen: welcher Token
 * im Fragment steht und welchen Status der Server meldet. Das ist eine Zustandsmaschine und
 * keine Navigation — ein Router wäre eine zweite Wahrheit über denselben Zustand.
 *
 * **Unbekanntes wird zum Startbildschirm, nicht zu einem Versuch** (fail closed). Ein halb
 * kopierter Link soll nicht in einer Ansicht landen, die eine Berechtigung behauptet.
 */

export type Route =
  | { readonly kind: 'start' }
  | { readonly kind: 'admin' }
  /**
   * Das neutrale Testbrett (Zyklus 10b) — eine Entwicklungsansicht **ohne** Partiedaten.
   *
   * Sie braucht keine Berechtigung, weil sie nichts zeigt, was einer Partie gehört: ein leeres
   * Brett mit seinen Feldnamen. Sie ist der Gegenstand, von dem die Designabstimmung ausgeht
   * (`initPrompt.md`, Ergänzungsblock, Schritt 3).
   */
  | { readonly kind: 'board' }
  | { readonly kind: 'host'; readonly hostToken: string }
  | { readonly kind: 'join'; readonly inviteToken: string };

const START: Route = { kind: 'start' };

/**
 * Das Alphabet von base64url (`apps/server/src/tokens.ts`).
 *
 * Eine **Formprüfung** und keine Sicherheitsprüfung: entschieden wird am Server. Ihr Zweck ist
 * eine verständliche Meldung, bevor eine Anfrage hinausgeht — ein in einem Chat abgeschnittener
 * Link soll nicht als „nicht berechtigt" erscheinen. Ohne Längenschranke, weil die einzige
 * Quelle der Wahrheit für die Form (`inviteTokenSchema` in `@flaghex/contracts`) nur
 * „nicht leer" verlangt; eine Zahl hier wäre eine zweite, die still veralten könnte.
 */
const TOKEN_SHAPE = /^[A-Za-z0-9_-]+$/;

/** Teilt das Fragment in Segmente — ohne führende Raute, ohne leere Teile am Rand. */
function segmentsOf(fragment: string): readonly string[] {
  const withoutHash = fragment.startsWith('#') ? fragment.slice(1) : fragment;
  return withoutHash.split('/').filter((segment) => segment !== '');
}

function tokenRoute(kind: 'host' | 'join', token: string | undefined): Route {
  if (token === undefined || !TOKEN_SHAPE.test(token)) {
    return START;
  }
  return kind === 'host' ? { kind, hostToken: token } : { kind, inviteToken: token };
}

/** Liest die Route aus einem Fragment. Mit oder ohne führende Raute. */
export function parseFragment(fragment: string): Route {
  const segments = segmentsOf(fragment);
  const [head, second] = segments;

  if (head === 'admin' || head === 'board') {
    // Kein zusätzliches Segment: `#/admin/irgendwas` ist kein Weg, den diese Anwendung kennt.
    return segments.length === 1 ? { kind: head } : START;
  }
  if ((head === 'host' || head === 'join') && segments.length === 2) {
    return tokenRoute(head, second);
  }
  return START;
}
